import clsx from 'clsx'
import { upperFirst } from 'lodash-es'
import pluralize from 'pluralize'
import type { MouseEvent } from 'react'

import { Icon } from '@fv/client-components'
import type { ClientPackagingType, ProductCatalogEntry } from '@fv/client-types'

import { ActionList, ActionListItem } from '@/components/ActionList'
import { DividedContent } from '@/components/DividedContent'
import { CheckboxField } from '@/components/inputs/CheckboxField'
import { PillHeader } from '@/components/PillHeader'
import { Pinline } from '@/components/Pinline'
import { useTypeNames } from '@/hooks/settings'

import { HasPermission } from '../auth'
import { useHasPermission } from '../auth/hooks'
import { getDimensions } from './commodityUtils'
import { useDeleteHandlingUnit } from './useDeleteHandlingUnit'
import { useProductCatalog } from './useProductCatalog'

const _NA = 'NA'

type ProductContainsProps = {
  saidToContain: string
  saidToContainPackagingType: ClientPackagingType | ''
}

const ProductContains = ({
  saidToContain,
  saidToContainPackagingType,
}: ProductContainsProps) => {
  const { packageName: getPackageName } = useTypeNames()
  const quantity = Number(saidToContain || 2)
  const packageName = getPackageName(saidToContainPackagingType)
  const packageLabel = pluralize(packageName, quantity)
  const contains = `${saidToContain} ${upperFirst(packageLabel)}`.trim()

  return <div>Contains: {contains || _NA}</div>
}

type ProductPackageProps = {
  package: ClientPackagingType | ''
  pieces: string
}

const ProductPackage = (props: ProductPackageProps) => {
  const { packageName: getPackageName } = useTypeNames()

  if (!props.package) return null

  const quantity = Number(props.pieces || 2)
  const packageName = getPackageName(props.package)
  const packageLabel = pluralize(packageName, quantity)

  return <div>{`${props.pieces} ${upperFirst(packageLabel)}`.trim()}</div>
}

type ProductProps = {
  product: ProductCatalogEntry
}

export const Product = ({ product }: ProductProps) => {
  const deleteProduct = useDeleteHandlingUnit()
  const dimensions = getDimensions(product)
  const editProduct = useProductCatalog(s => s.edit)
  const { selectProduct, setProduct, selectedIds, toggleSelect } =
    useProductCatalog(s => s)
  const canManageProducts = useHasPermission('manage-products')
  const isManageMode = !setProduct && canManageProducts
  const isSelected = selectedIds.includes(product._id)

  function selectHandlingUnit() {
    if (deleteProduct.isPending) return
    if (setProduct) {
      selectProduct(product)
    } else if (canManageProducts) {
      editProduct(product._id)
    }
  }

  function editHandlingUnit(e: MouseEvent<HTMLAnchorElement>) {
    e.stopPropagation()
    if (deleteProduct.isPending) return
    editProduct(product._id)
  }

  function deleteHandlingUnit(e: MouseEvent<HTMLAnchorElement>) {
    e.stopPropagation()
    if (deleteProduct.isPending) return

    const isConfirmed = window.confirm(
      `Are you sure you want to delete ${product.description} from your product catalog?`,
    )

    if (!isConfirmed) return
    deleteProduct.mutate(product._id)
  }

  return (
    <div
      className={clsx(
        'hover:border-fv-blue group relative mt-[2.25rem] cursor-pointer border bg-fv-blue-50 px-4 pb-10 first:mt-0',
        isSelected ? 'border-fv-blue' : 'border-[#bde1ef]',
      )}
      onClick={selectHandlingUnit}
    >
      <PillHeader
        st
        className="beer-container group-hover:border-fv-beer-dark relative -left-6 -top-4 flex items-center"
      >
        {isManageMode && (
          <div className="" onClick={e => e.stopPropagation()}>
            <CheckboxField
              name={`select-product-${product._id}`}
              checked={isSelected}
              onChange={() => toggleSelect(product._id)}
              alternate
            />
          </div>
        )}
        <p className="mb-0 flex">
          <Icon
            icon="forklift"
            className={clsx(
              'text-fv-gray-dark',
              product.description && 'mt-[.175rem]',
            )}
            transform="right-3"
          />
          <span>{product.description}</span>
        </p>
      </PillHeader>

      <DividedContent theme="blue">
        <ProductPackage {...product} />
        <div>Dimensions: {dimensions || _NA}</div>
        <div>
          Total weight: {product.weight ? `${product.weight} lbs` : _NA}
        </div>
        <div>Freight class: {product.freightClass || _NA}</div>
      </DividedContent>

      <Pinline rule className="mt-2 mb-4 !border-[#bde1ef]" />

      <DividedContent theme="blue">
        <div>NMFC#: {product.nmfc || _NA}</div>
        <ProductContains {...product} />
        <div>Stackable: {product.stackable}</div>
        <div>Hazardous: {product.hazardous}</div>
      </DividedContent>

      {product.hazardous && <Pinline className="my-2 !border-[#bde1ef]" />}

      {product.hazardous && (
        <DividedContent theme="blue">
          <div>Hazmat ID: {product.hazmatId || _NA}</div>
          <div>Hazard class: {product.hazardClass || _NA}</div>
          <div>Packing group: {product.packingGroup || _NA}</div>

          {product.specialPermit && (
            <div>Special permit: {product.specialPermit}</div>
          )}
        </DividedContent>
      )}

      <HasPermission name="manage-products">
        <ActionList className="absolute bottom-3 right-3">
          <ActionListItem onClick={editHandlingUnit} icon="pen" />
          <ActionListItem
            icon={deleteProduct.isPending ? 'spinner' : 'trash'}
            iconClass="text-fv-orange-bright"
            onClick={deleteHandlingUnit}
          />
        </ActionList>
      </HasPermission>
    </div>
  )
}
